import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/constants";
import { 
  Copy, Check, Bot, MessageSquare, Phone, Mic,
  ChevronDown, ChevronUp, ExternalLink
} from "lucide-react";

const GBPPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState({});

  useEffect(() => {
    document.title = "GBP Posts | Digital Media Ninja Admin";
    fetchPosts();
  }, []);

  const fetchPosts = async (category = null) => {
    try {
      const params = category ? `?category=${category}` : '';
      const response = await axios.get(`${API}/gbp-posts${params}`);
      setPosts(response.data.posts);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Failed to fetch GBP posts:", error);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category) {
      fetchPosts(category);
    } else {
      fetchPosts();
    }
  };

  const copyToClipboard = async (text, postId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(postId);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const toggleExpand = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'llm_optimization': return <Bot className="w-5 h-5" />;
      case 'aeo': return <MessageSquare className="w-5 h-5" />;
      case 'ai_voice_agents': return <Phone className="w-5 h-5" />;
      case 'ai_receptionist': return <Mic className="w-5 h-5" />;
      default: return <MessageSquare className="w-5 h-5" />;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'llm_optimization': 'LLM Optimization',
      'aeo': 'Answer Engine Optimization (AEO)',
      'ai_voice_agents': 'AI Voice Agents',
      'ai_receptionist': 'AI Receptionist'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'llm_optimization': 'bg-purple-500/10 border-purple-500/30 text-purple-400',
      'aeo': 'bg-blue-500/10 border-blue-500/30 text-blue-400',
      'ai_voice_agents': 'bg-orange-500/10 border-orange-500/30 text-orange-400',
      'ai_receptionist': 'bg-green-500/10 border-green-500/30 text-green-400'
    };
    return colors[category] || 'bg-gray-500/10 border-gray-500/30 text-gray-400';
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="gbp-posts-page">
      <Helmet>
        <title>GBP Posts | Digital Media Ninja Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container-custom">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">Google Business Profile Posts</h1>
            <p className="text-gray-400 text-lg">
              Ready-to-publish content for your GBP. Click to copy and paste directly into Google Business Profile.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <Button
              variant={activeCategory === null ? "default" : "outline"}
              onClick={() => handleCategoryChange(null)}
              className={activeCategory === null ? "bg-[#00FF85] text-black" : "border-white/20 text-white"}
            >
              All Posts ({posts.length})
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={activeCategory === cat ? "default" : "outline"}
                onClick={() => handleCategoryChange(cat)}
                className={activeCategory === cat ? "bg-[#00FF85] text-black" : "border-white/20 text-white"}
              >
                {getCategoryIcon(cat)}
                <span className="ml-2">{getCategoryLabel(cat)}</span>
              </Button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="space-y-6">
            {posts.map((post) => (
              <Card 
                key={post.id} 
                className={`bg-[#0A0A0A] border ${getCategoryColor(post.category_key).split(' ')[1]} overflow-hidden`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${getCategoryColor(post.category_key)}`}>
                        {getCategoryIcon(post.category_key)}
                      </div>
                      <div>
                        <span className={`text-xs font-medium ${getCategoryColor(post.category_key).split(' ')[2]}`}>
                          {post.category}
                        </span>
                        <CardTitle className="text-lg text-white mt-1">{post.title}</CardTitle>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(post.content, post.id)}
                      className="border-white/20 text-white hover:bg-white/10 shrink-0"
                    >
                      {copiedId === post.id ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-400" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Post
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className={`bg-black/50 rounded-lg p-4 border border-white/5 ${
                      expandedPosts[post.id] ? '' : 'max-h-48 overflow-hidden relative'
                    }`}
                  >
                    <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans">
                      {post.content}
                    </pre>
                    {!expandedPosts[post.id] && (
                      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/80 to-transparent" />
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(post.id)}
                      className="text-gray-400 hover:text-white"
                    >
                      {expandedPosts[post.id] ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          Show Full Post
                        </>
                      )}
                    </Button>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">CTA: {post.cta}</span>
                      <a 
                        href={post.cta_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#00FF85] text-xs hover:underline flex items-center gap-1"
                      >
                        Preview Link <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-16 p-6 bg-[#0A0A0A] rounded-2xl border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">How to Post to Google Business Profile</h2>
            <ol className="space-y-3 text-gray-300">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">1</span>
                <span>Go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-[#00FF85] hover:underline">business.google.com</a> and select your business</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">2</span>
                <span>Click "Add update" or "Create post" in the left sidebar</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">3</span>
                <span>Choose post type (Update, Offer, Event, or Product)</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">4</span>
                <span>Click "Copy Post" above and paste the content</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">5</span>
                <span>Add a relevant image (AI-themed, office, or service-related)</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">6</span>
                <span>Add the CTA button with the provided link</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF85]/20 text-[#00FF85] text-sm flex items-center justify-center">7</span>
                <span>Click "Publish" - Posts are live for 7 days</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GBPPostsPage;
